import { Alert, Button, Card, CommonProp, DataRow, DataTable, IconAdd, Loading } from '@acin/ui-core';
import { SelectOption, SelectOptionValue } from '@acin/ui-core/types/src/components/Select/components/Select';
import React from 'react';
import { useClientsQuery } from '../../../graphql/queries/getClients.generated';
import { useRegulationsQuery } from '../../../graphql/queries/getRegulations.generated';
import { CreateMappingsJobModal } from '../../CreateMappingsJobModal';
import { StatusBadge } from '../../StatusBadge';
import { useInternalMappingsJobsQuery } from '../graphql/queries/getInternalMappingsJobs.generated';
import { createClientCodeFilter, createRegulationIdFilter } from '../hooks/filterConstants';
import { TABLE_HEADERS } from '../utils';
import * as SC from './ControlMappingsRuns.styled';

const ALL_CLIENTS_OPTION: SelectOption = { value: 'ALL', label: 'All Clients' };
const ALL_REGULATIONS_OPTION: SelectOption = { value: 'ALL', label: 'All Regulations' };

export interface ControlMappingsRunsProps extends CommonProp {
  className?: string;
}

export const ControlMappingsRuns: React.FC<ControlMappingsRunsProps> = (props) => {
  const { className } = props;
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [selectedClientCode, setSelectedClientCode] = React.useState<string | undefined>(undefined);
  const [selectedRegulationId, setSelectedRegulationId] = React.useState<string | undefined>(undefined);

  const { data: clientsData } = useClientsQuery({ variables: { first: 100 } });
  const { data: regulationsData } = useRegulationsQuery({ variables: { first: 100 } });

  const clientOptions: SelectOption[] = [
    ALL_CLIENTS_OPTION,
    ...(clientsData?.clients?.edges ?? []).map((edge) => ({
      value: edge.node.code,
      label: edge.node.shortName
        ? `${edge.node.name} (${edge.node.shortName})`
        : `${edge.node.name} (${edge.node.code})`,
    })),
  ];

  const regulationOptions: SelectOption[] = [
    ALL_REGULATIONS_OPTION,
    ...(regulationsData?.internalRegulations?.edges ?? [])
      .filter((edge) => !!edge.node.id)
      .map((edge) => ({
        value: edge.node.id as string,
        label: edge.node.title,
      })),
  ];

  const handleClientChange = (value: SelectOptionValue) =>
    setSelectedClientCode(value === 'ALL' ? undefined : String(value));
  const handleRegulationChange = (value: SelectOptionValue) =>
    setSelectedRegulationId(value === 'ALL' ? undefined : String(value));

  const filterGroups = [
    ...(selectedClientCode ? [createClientCodeFilter(selectedClientCode)] : []),
    ...(selectedRegulationId ? [createRegulationIdFilter(selectedRegulationId)] : []),
  ];

  const { data, error, loading } = useInternalMappingsJobsQuery({
    variables: { input: { filters: { groups: filterGroups } } },
  });

  const validRows: DataRow[] = [];

  data?.internalMappingsJobs?.edges.forEach((edge) => {
    const node = edge?.node;
    if (!node) {
      return;
    }

    validRows.push({
      clientCode: node.clientCode,
      regulationName: node.regulationName,
      documentName: node.documentName ?? '-',
      createdAt: node.createdAt,
      publishedAt: node.publishedAt ?? '-',
      status: <StatusBadge status={node.status} />,
    });
  });

  const existingJobs = (data?.internalMappingsJobs?.edges ?? []).flatMap((edge) => (edge?.node ? [edge.node] : []));

  const renderContent = () => {
    if (loading) {
      return <Loading />;
    }

    if (error) {
      return <Alert status="error" title="Could not load mappings jobs" />;
    }

    if (validRows.length === 0) {
      return <Alert status="info" title="No mappings jobs found" />;
    }

    return (
      <Card>
        <DataTable headers={TABLE_HEADERS} rows={validRows} variation="primary" onRowClick={() => {}} />
      </Card>
    );
  };

  return (
    <SC.ControlMappingsRuns data-testid="control-mappings-runs" className={className}>
      <SC.Actions>
        <SC.Filters>
          <SC.ClientFilter
            onChange={handleClientChange}
            options={clientOptions}
            value={selectedClientCode ?? 'ALL'}
            prefix="Client"
            data-testid="control-mappings-runs-client-filter"
          />
          <SC.RegulationFilter
            onChange={handleRegulationChange}
            options={regulationOptions}
            value={selectedRegulationId ?? 'ALL'}
            prefix="Regulation"
            data-testid="control-mappings-runs-regulation-filter"
          />
        </SC.Filters>
        <Button
          variant="primary"
          label="Create new client mappings"
          icon={() => <IconAdd />}
          onClick={() => setShowCreateModal(true)}
        />
      </SC.Actions>
      {renderContent()}
      <CreateMappingsJobModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        existingJobs={existingJobs}
      />
    </SC.ControlMappingsRuns>
  );
};
